// Simple Cart Drawer JavaScript
class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.overlay = document.querySelector('[data-cart-overlay]');
    this.itemsContainer = document.querySelector('[data-cart-items]');
    this.checkoutBtn = document.querySelector('[data-cart-checkout]');
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.loadCartData();
    this.setupCartEventListeners();
  }

  bindEvents() {
    // Close drawer events
    document.querySelector('[data-cart-close]')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    
    // Cart icon click
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-icon]') || e.target.closest('.cart-count')) {
          e.preventDefault();
        this.open();
      }
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
    
    // Quantity controls
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-cart-qty-plus]')) {
        this.updateQuantity(e.target.dataset.key, 'plus');
      }
      if (e.target.matches('[data-cart-qty-minus]')) {
        this.updateQuantity(e.target.dataset.key, 'minus');
      }
      if (e.target.matches('[data-cart-remove]') || e.target.closest('[data-cart-remove]')) {
        const removeButton = e.target.matches('[data-cart-remove]') ? e.target : e.target.closest('[data-cart-remove]');
        this.removeItem(removeButton.dataset.cartRemove);
      }
    });

    // Quantity input changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-cart-qty-input]')) {
        const quantity = parseInt(e.target.value);
        if (quantity > 0) {
          this.updateQuantity(e.target.dataset.key, quantity);
        }
      }
    });

    // Checkout button
    this.checkoutBtn?.addEventListener('click', () => {
      window.location.href = '/checkout';
    });
  }

  open() {
    if (this.drawer) {
      this.drawer.classList.add('active');
      document.body.style.overflow = 'hidden';
      // Load cart data immediately
      this.loadCartData();
    }
  }

  openWithData(cartData) {
    if (this.drawer) {
      this.drawer.classList.add('active');
      document.body.style.overflow = 'hidden';
      
      // Display cart immediately with fresh data
      this.updateCartDisplay(cartData);
    }
  }

  setupCartEventListeners() {
    // Simple listener for cart changes
    document.addEventListener('cartcount:update', (e) => {
      console.log('Cart count update detected:', e.detail.item_count);
    });
  }

  close() {
    if (this.drawer) {
      this.drawer.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  isOpen() {
    return this.drawer?.classList.contains('active');
  }

  async loadCartData(forceRefresh = false) {
    // Always load fresh data - no caching to prevent delays
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateCartDisplay(cart);
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  }

  updateCartDisplay(cart) {
    if (!this.itemsContainer) return;

    if (cart.item_count === 0) {
      this.itemsContainer.innerHTML = `
        <div class="cart-drawer__empty">
          <h2>Your cart is empty</h2>
        </div>
      `;
      this.hideFooter();
      return;
    }

    // Clear empty state if it exists
    const emptyState = this.itemsContainer.querySelector('.cart-drawer__empty');
    if (emptyState) {
      emptyState.remove();
    }

    // Render cart items
    this.itemsContainer.innerHTML = cart.items.map(item => this.renderCartItem(item)).join('');
    this.showFooter();
    
    // Update cart total if available
    if (cart.total_price) {
      this.updateCartTotal(cart.total_price);
    }
  }

  renderCartItem(item) {
    const imageUrl = item.image ? item.image.replace('.jpg', '_150x150.jpg') : '';
    const imageHtml = item.image 
      ? `<img src="${imageUrl}" alt="${this.escapeHtml(item.image.alt)}" loading="lazy">`
      : `<div class="cart-drawer__item-placeholder">
           <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
             <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2"/>
             <path d="M9 9h6v6H9z" stroke="currentColor" stroke-width="2"/>
           </svg>
         </div>`;

    return `
      <div class="cart-drawer__item" data-cart-item="${item.key}">
        <a href="${item.url}" class="cart-drawer__item-image">
          ${imageHtml}
        </a>
        
        <div class="cart-drawer__item-details">
          <a href="${item.url}" class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a>
          <p class="cart-drawer__item-sku">SKU ${this.escapeHtml(item.sku || 'N/A')}</p>
          
          <div class="cart-drawer__item-controls">
            <div class="cart-drawer__quantity">
              <button class="cart-drawer__qty-btn" data-cart-qty-minus data-key="${item.key}">-</button>
              <input type="number" class="cart-drawer__qty-input" value="${item.quantity}" data-cart-qty-input data-key="${item.key}" min="1">
              <button class="cart-drawer__qty-btn" data-cart-qty-plus data-key="${item.key}">+</button>
            </div>
            
            <div class="cart-drawer__item-price">
              <span class="cart-drawer__price-label">Subtotal</span>
              <span class="cart-drawer__price-value">${this.formatMoney(item.price * item.quantity)}</span>
            </div>
            
            <button class="cart-drawer__remove" data-cart-remove="${item.key}" aria-label="Remove item">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  async updateQuantity(key, quantity) {
    const currentQty = this.getCurrentQuantity(key);
    let newQty;

    if (quantity === 'plus') {
      newQty = currentQty + 1;
    } else if (quantity === 'minus') {
      newQty = Math.max(1, currentQty - 1);
    } else {
      newQty = quantity;
    }

    await this.updateCart(key, newQty);
  }

  async removeItem(key) {
    await this.updateCart(key, 0);
  }

  async updateCart(key, quantity) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          updates: {
            [key]: quantity
          }
        })
      });

      if (response.ok) {
        const cart = await response.json();
        this.updateCartDisplay(cart);
        
        // Trigger cart count update event
        const countEvent = new CustomEvent("cartcount:update", {
          detail: cart
        });
        window.dispatchEvent(countEvent);
      } else {
        throw new Error('Failed to update cart');
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  getCurrentQuantity(key) {
    const input = document.querySelector(`[data-cart-qty-input][data-key="${key}"]`);
    return input ? parseInt(input.value) : 1;
  }

  updateCartTotal(total) {
    const totalElements = document.querySelectorAll('[data-cart-total]');
    totalElements.forEach(element => {
      element.textContent = this.formatMoney(total);
    });

    // Update checkout button text
    const checkoutBtn = document.querySelector('[data-cart-checkout]');
    if (checkoutBtn) {
      const totalSpan = checkoutBtn.querySelector('[data-cart-total]');
      if (totalSpan) {
        totalSpan.textContent = this.formatMoney(total);
      } else {
        checkoutBtn.textContent = `Proceed to Checkout - ${this.formatMoney(total)}`;
      }
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  showFooter() {
    const footer = document.querySelector('[data-cart-footer]');
    if (footer) {
      footer.style.display = 'block';
    }
  }

  hideFooter() {
    const footer = document.querySelector('[data-cart-footer]');
    if (footer) {
      footer.style.display = 'none';
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (!window.cartDrawerInstance) {
    window.cartDrawerInstance = new CartDrawer();
  }
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.cartDrawerInstance) {
      window.cartDrawerInstance = new CartDrawer();
    }
  });
} else {
  if (!window.cartDrawerInstance) {
    window.cartDrawerInstance = new CartDrawer();
  }
}

// Export for global access
window.CartDrawer = CartDrawer;